import type { Data } from "@measured/puck";

/** Zone racine Puck (parité FieldsPanel / lib interne Puck). */
export const PUCK_ROOT_ZONE = "root:default-zone";

export type PuckTreeNode = { type?: string; props?: Record<string, unknown> };

/** Config minimale pour lire les `allow` des slots. */
export type PuckSlotAllowConfig = {
  components: Record<
    string,
    { fields?: Record<string, { type?: string; allow?: readonly string[] }> }
  >;
};

/**
 * Types de blocs « contextuels » (shell / catalogue) : collage autorisé mais
 * toast d'avertissement soft (souvent hors contexte hors header/footer/flow).
 */
export const CONTEXTUAL_BLOCK_TYPES = new Set<string>([
  "SiteLogo",
  "SiteMenu",
  "HeaderCta",
  "HeaderUserAuth",
  "HeaderBasket",
  "HeaderLanguage",
  "FooterContent",
  "CatalogueListing",
  "CatalogueFilters",
  "CatalogueSearchResults",
  "CatalogueItemDetail",
  "CatalogueDecoupage",
  "CatalogueDecoupages",
  "Breadcrumb",
]);

export type PuckNodePathSegment = {
  type: string;
  label?: string;
  id: string;
  zone: string;
  index: number;
};

function isSlotArray(value: unknown): value is PuckTreeNode[] {
  return (
    Array.isArray(value) &&
    value.every(
      (v) =>
        v != null &&
        typeof v === "object" &&
        typeof (v as PuckTreeNode).type === "string" &&
        typeof (v as PuckTreeNode).props === "object",
    )
  );
}

function getNodeId(node: PuckTreeNode): string | null {
  const id = node.props?.id;
  return typeof id === "string" && id.length > 0 ? id : null;
}

function walkPuckTree(
  nodes: PuckTreeNode[] | undefined,
  visit: (node: PuckTreeNode) => void,
): void {
  if (!Array.isArray(nodes)) return;
  for (const node of nodes) {
    if (!node || typeof node !== "object") continue;
    visit(node);
    const props = node.props;
    if (!props || typeof props !== "object") continue;
    for (const value of Object.values(props)) {
      if (isSlotArray(value)) walkPuckTree(value, visit);
    }
  }
}

/** Retrouve un bloc par `props.id` n'importe où dans l'arbre (slots imbriqués). */
export function findPuckNodeById(
  data: Data,
  nodeId: string,
): PuckTreeNode | null {
  let found: PuckTreeNode | null = null;
  walkPuckTree(data.content as PuckTreeNode[], (node) => {
    if (getNodeId(node) === nodeId) found = node;
  });
  return found;
}

function getPuckSlotItems(
  data: Data,
  nodeId: string,
  slotKey: string,
): PuckTreeNode[] {
  const node = findPuckNodeById(data, nodeId);
  if (!node?.props) return [];
  const slot = node.props[slotKey];
  return isSlotArray(slot) ? slot : [];
}

/** `true` si le slot nommé du bloc est vide ou absent. */
export function isPuckSlotEmpty(
  data: Data,
  nodeId: string,
  slotKey: string,
): boolean {
  return getPuckSlotItems(data, nodeId, slotKey).length === 0;
}

/** Nombre d'éléments dans un slot (frères). */
export function getPuckSlotChildCount(
  data: Data,
  nodeId: string,
  slotKey: string,
): number {
  return getPuckSlotItems(data, nodeId, slotKey).length;
}

/**
 * Chemin racine → bloc cible avec `zone` + `index` pour resélection via `setUi`.
 * `labelResolver` permet d'afficher le libellé humain du type (ex. « Conteneur »).
 */
export function findPuckNodePath(
  data: Data,
  targetId: string,
  labelResolver?: (type: string) => string | undefined,
): PuckNodePathSegment[] | null {
  let result: PuckNodePathSegment[] | null = null;

  function search(
    nodes: PuckTreeNode[],
    zone: string,
    path: PuckNodePathSegment[],
  ): boolean {
    for (let index = 0; index < nodes.length; index++) {
      const node = nodes[index];
      const id = getNodeId(node);
      if (!id) continue;

      const type = node.type ?? "";
      const segment: PuckNodePathSegment = {
        type,
        label: labelResolver?.(type) ?? type,
        id,
        zone,
        index,
      };
      const nextPath = [...path, segment];

      if (id === targetId) {
        result = nextPath;
        return true;
      }

      const props = node.props;
      if (props && typeof props === "object") {
        for (const [key, value] of Object.entries(props)) {
          if (isSlotArray(value) && search(value, `${id}:${key}`, nextPath)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  const content = data.content as PuckTreeNode[];
  if (!Array.isArray(content)) return null;
  search(content, PUCK_ROOT_ZONE, []);
  return result;
}

/** Blocs d'une zone Puck (`root:default-zone` ou `<parentId>:<slotKey>`). */
export function getPuckZoneNodes(data: Data, zone: string): PuckTreeNode[] {
  if (zone === PUCK_ROOT_ZONE) {
    const content = data.content;
    return Array.isArray(content) ? (content as PuckTreeNode[]) : [];
  }
  const colon = zone.lastIndexOf(":");
  if (colon <= 0) return [];
  const parentId = zone.slice(0, colon);
  const slotKey = zone.slice(colon + 1);
  const parent = findPuckNodeById(data, parentId);
  if (!parent?.props) return [];
  const slot = parent.props[slotKey];
  return isSlotArray(slot) ? slot : [];
}

/** Bloc à l'index d'une zone (utilisé pour détecter une duplication Puck). */
export function getPuckNodeAtZoneIndex(
  data: Data,
  zone: string,
  index: number,
): PuckTreeNode | null {
  const nodes = getPuckZoneNodes(data, zone);
  const node = nodes[index];
  return node && typeof node === "object" ? node : null;
}

/**
 * Carte `ancien props.id → nouveau props.id` pour un doublon de sous-arbre
 * (bloc racine dupliqué + tous les enfants de slots, parallèlement).
 */
export function mapDuplicateBlockIds(
  source: PuckTreeNode,
  duplicate: PuckTreeNode,
): Record<string, string> {
  const out: Record<string, string> = {};

  function walk(a: PuckTreeNode, b: PuckTreeNode): void {
    const fromId = getNodeId(a);
    const toId = getNodeId(b);
    if (fromId && toId && fromId !== toId) out[fromId] = toId;

    const aProps = a.props;
    const bProps = b.props;
    if (!aProps || !bProps || typeof aProps !== "object" || typeof bProps !== "object") {
      return;
    }
    for (const [key, aVal] of Object.entries(aProps)) {
      const bVal = bProps[key];
      if (!isSlotArray(aVal) || !isSlotArray(bVal)) continue;
      const len = Math.min(aVal.length, bVal.length);
      for (let i = 0; i < len; i++) walk(aVal[i], bVal[i]);
    }
  }

  walk(source, duplicate);
  return out;
}

/**
 * Remplace récursivement chaque `props.id` (format Puck : `<type>-<uuid>`)
 * et renvoie la carte `ancien → nouveau` pour le remapping i18n.
 */
export function regeneratePuckSubtreeIds(node: PuckTreeNode): {
  node: PuckTreeNode;
  mappings: Record<string, string>;
} {
  const mappings: Record<string, string> = {};

  function regen(n: PuckTreeNode): PuckTreeNode {
    const type = n.type ?? "Block";
    const oldId = getNodeId(n);
    const props = { ...(n.props ?? {}) } as Record<string, unknown>;
    const newId = `${type}-${crypto.randomUUID()}`;
    props.id = newId;
    if (oldId) mappings[oldId] = newId;

    for (const [key, value] of Object.entries(props)) {
      if (!isSlotArray(value)) continue;
      props[key] = value.map((child) => regen(child));
    }

    return { ...n, props };
  }

  return { node: regen(node), mappings };
}

/** Liste mutable des nœuds d'une zone dans un `Data` déjà cloné. */
function getPuckZoneNodesMutable(
  data: Data,
  zone: string,
): PuckTreeNode[] | null {
  if (zone === PUCK_ROOT_ZONE) {
    if (!Array.isArray(data.content)) {
      (data as { content: PuckTreeNode[] }).content = [];
    }
    return data.content as PuckTreeNode[];
  }
  const colon = zone.lastIndexOf(":");
  if (colon <= 0) return null;
  const parentId = zone.slice(0, colon);
  const slotKey = zone.slice(colon + 1);
  const parent = findPuckNodeById(data, parentId);
  if (!parent) return null;
  if (!parent.props || typeof parent.props !== "object") {
    parent.props = {};
  }
  if (!isSlotArray(parent.props[slotKey])) {
    parent.props[slotKey] = [];
  }
  return parent.props[slotKey] as PuckTreeNode[];
}

/** Insert immuable d'un nœud dans une zone (`root:default-zone` ou slot). */
export function insertPuckNodeAtZone(
  data: Data,
  zone: string,
  index: number,
  node: PuckTreeNode,
): Data {
  const next = structuredClone(data) as Data;
  const nodes = getPuckZoneNodesMutable(next, zone);
  if (!nodes) return data;
  const i = Math.max(0, Math.min(index, nodes.length));
  nodes.splice(i, 0, node);
  return next;
}

/** Suppression immuable d'un nœud à l'index d'une zone. */
export function removePuckNodeAtZone(
  data: Data,
  zone: string,
  index: number,
): Data {
  const next = structuredClone(data) as Data;
  const nodes = getPuckZoneNodesMutable(next, zone);
  if (!nodes || index < 0 || index >= nodes.length) return data;
  nodes.splice(index, 1);
  return next;
}

/**
 * `allow` du slot cible, ou `null` si zone racine / slot sans liste
 * (= pas de restriction connue).
 */
export function getZoneSlotAllow(
  data: Data,
  zone: string,
  config: PuckSlotAllowConfig,
): readonly string[] | null {
  if (zone === PUCK_ROOT_ZONE) return null;
  const colon = zone.lastIndexOf(":");
  if (colon <= 0) return [];
  const parentId = zone.slice(0, colon);
  const slotKey = zone.slice(colon + 1);
  const parent = findPuckNodeById(data, parentId);
  const parentType = parent?.type;
  if (!parentType) return [];
  const field = config.components[parentType]?.fields?.[slotKey];
  if (field?.type === "slot" && Array.isArray(field.allow)) {
    return field.allow;
  }
  return null;
}

/** `true` si `nodeType` peut être collé dans la zone (selon `allow` du slot). */
export function canPasteIntoZone(
  data: Data,
  zone: string,
  nodeType: string,
  config: PuckSlotAllowConfig,
): boolean {
  const allow = getZoneSlotAllow(data, zone, config);
  if (allow == null) return true;
  return allow.includes(nodeType);
}

/** Collecte tous les `props.id` d'un sous-arbre (racine incluse). */
export function collectPuckSubtreeIds(node: PuckTreeNode): string[] {
  const ids: string[] = [];
  walkPuckTree([node], (n) => {
    const id = getNodeId(n);
    if (id) ids.push(id);
  });
  return ids;
}

/** Réécrit les clés `strings` après duplication de blocs (R2, cache local). */
export function remapStringsForBlockCopy(
  strings: Record<string, string>,
  mappings: Record<string, string>,
): Record<string, string> {
  const out = { ...strings };
  for (const [fromId, toId] of Object.entries(mappings)) {
    if (!fromId || !toId || fromId === toId) continue;
    const prefix = `${fromId}.`;
    for (const [key, value] of Object.entries(strings)) {
      if (key.startsWith(prefix)) {
        out[`${toId}.${key.slice(prefix.length)}`] = value;
      }
    }
  }
  return out;
}
